import { useActiveBookingContext, useBookingEngineStateContext, usePropertyContext } from "@app/app/hooks";
import { IBookingEngineState } from "@app/app/types";
import { Label } from "@app/common/components/form/Label";
import { FilterIcon } from "@app/common/components/icons/FilterIcon";
import { BookableType, type IBookableItemsFilter, type IFilter } from "@app/common/types";
import { Button } from "@app/componentLibrary/ui/button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogHeightModes,
    DialogTitle,
    DialogTrigger,
    DialogVerticalPositions,
} from "@app/componentLibrary/ui/dialog";
import { Flex } from "@app/componentLibrary/ui/flex";
import { Checkbox } from "@app/componentLibrary/ui/form/Checkbox";
import { Bold, ThemeTitle } from "@app/componentLibrary/ui/text";
import { useEffect, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import { ControlledFormField } from "./form/ControlledFormField";

export interface IFilterForm {
    rooms: boolean | undefined;
    packages: boolean | undefined;
    filters: { [key: string]: boolean | undefined };
}

const getFilterState = (form: IFilterForm): IBookableItemsFilter => {
    const type =
        form.rooms && !form.packages
            ? BookableType.Room
            : !form.rooms && form.packages
            ? BookableType.Package
            : form.rooms && form.packages
            ? "rp"
            : undefined;
    const filters = Object.keys(form.filters).filter((id) => !!form.filters[id]);

    return { type, filters };
};

export interface IFilterModalProps {
    triggerButtonClassName?: string;
}

export const FilterModal = ({ triggerButtonClassName }: IFilterModalProps) => {
    const { activeProperty } = usePropertyContext();
    const { getState, availability } = useBookingEngineStateContext();
    const { activeBookingIndex } = useActiveBookingContext();
    const { setValue } = useFormContext<IBookingEngineState>();
    const { t } = useTranslation(["common", "name"]);
    const { control, handleSubmit, reset, getValues } = useForm<IFilterForm>();
    const [open, setOpen] = useState(false);
    const rooms = availability[activeBookingIndex]?.rooms || [];
    const filterState = getState()?.bookableItemsFilter || { filters: [] };
    const filters = filterState.filters.reduce((obj: any, el: string) => ({ ...obj, [el]: true }), {});

    const activeFilter = (f: IFilter, i: number, a: IFilter[]) => rooms.some((r) => r.filters.includes(f.id));

    const filterGroups = (activeProperty?.filter_groups || []).map((g) => ({ ...g, filters: g.filters.filter(activeFilter) }));
    const showFilterButton = filterGroups.filter((g) => !!g.filters.length).length > 0;

    const preSubmit = (data: IFilterForm) => {
        setValue("bookableItemsFilter", getFilterState(data));
        setOpen(false);
    };

    useEffect(() => {
        if (open) {
            reset({ rooms: true, packages: false, filters });
        }
    }, [open]);

    return showFilterButton ? (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    data-testid="filter-btn-icon"
                    size="icon"
                    variant="outline"
                    className={twMerge("mr-1 w-8 h-8 p-5 leading-3 bg-white hover:bg-gray-50", triggerButtonClassName)}
                >
                    <FilterIcon className="h-4 w-4" />
                </Button>
            </DialogTrigger>
            <DialogContent verticalPosition={DialogVerticalPositions.Bottom} heightMode={DialogHeightModes.Fit}>
                <DialogHeader>
                    <DialogTitle>{t("filters")}</DialogTitle>
                    <DialogDescription hidden>{t("filters")}</DialogDescription>
                </DialogHeader>
                <form
                    key={filterState?.filters?.length}
                    data-testid="filter-modal"
                    onSubmit={handleSubmit((data) => preSubmit(data))}
                    className="flex flex-col grow rounded-lg w-full h-auto overflow-auto py-4 items-start pb-16"
                    data-scrollable
                >
                    {/* Important: Below section is temporarily disabled till we support packages, do not remove */}

                    {/* <Title
                            className="text-zinc-950 text-xs font-bold uppercase leading-tight tracking-tight px-4 pt-6 mb-6"
                            color="slate"
                        >
                            {t("room", { ns: "backendTranslations" })}
                        </Title> */}
                    {/* <Flex alignItems="center" justifyContent="between" className="w-full h-8 p-6 px-4 pr-6 pb-4">
                            <Label data-testid="rooms-checkbox" className="flex items-center">
                                <ControlledFormField
                                    data-testid="rooms-checkbox-input"
                                    control={control}
                                    component={Checkbox}
                                    name="rooms"
                                    className="ml-2 mr-3 w-8 h-8"
                                />
                                {UtilsStr.capitalize(t("rooms"))}
                            </Label>
                            <Label data-testid="packages-checkbox" className="flex items-center">
                                <ControlledFormField
                                    data-testid="packages-checkbox-input"
                                    control={control}
                                    component={Checkbox}
                                    name="packages"
                                    className="ml-2 mr-3 w-8 h-8"
                                />
                                {UtilsStr.capitalize(t("packages"))}
                            </Label>
                        </Flex> */}
                    {/* <ContentDivider className="border-slate-400 px-4 my-6 pr-6" id="divider" /> */}
                    {filterGroups
                        .filter((g) => !!g.filters.length)
                        .map((filterGroup) => (
                            <div className="p-6 px-4 pr-6 pt-0" key={filterGroup.id}>
                                <ThemeTitle
                                    className="text-zinc-950 text-xs font-bold uppercase leading-tight tracking-tight mb-6"
                                    size="xs"
                                    color="slate"
                                >
                                    {t(filterGroup.id, { ns: "name" })}
                                </ThemeTitle>
                                {(filterGroup.filters || []).map((filter) => (
                                    <div key={filter.id} className="pb-2">
                                        <Label className="flex items-center text-neutral-900 text-base font-normal leading-none">
                                            <ControlledFormField
                                                data-testid={`filter-item-${filter.id}`}
                                                control={control}
                                                component={Checkbox}
                                                name={`filters.${filter.id}`}
                                                className="ml-2 mr-3 w-8 h-8"
                                            />
                                            {t(filter.id, { ns: "name" })}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        ))}
                    <Flex
                        justifyContent="center"
                        alignItems="center"
                        className="py-4 mt-auto bg-gradient-to-b from-white/90 to-white absolute bottom-0 rounded-b-2xl"
                    >
                        <div className="w-full px-6">
                            <DialogClose asChild>
                                <Button
                                    data-testid="done-btn"
                                    type="submit"
                                    className="w-full rounded-lg text-base font-medium leading-tight"
                                    size="lg"
                                >
                                    <Bold color="white">{t("done")}</Bold>
                                </Button>
                            </DialogClose>
                        </div>
                    </Flex>
                </form>
            </DialogContent>
        </Dialog>
    ) : null;
};
